import { computed } from 'vue';
import { debounce } from 'lodash';
import { isValidCanvasElement } from '@/components/Layers/utils';
import store from '@/store';
import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ElementType } from '@/typings/customilyElements/layer';
import type { LayersProps } from '../types';
import type { CanvasEx } from '@/typings/canvas';
import { getTreeItems } from '@/utils/elementsUtils';

export function useCanvasEvents(props: LayersProps) {
    const settings = computed(() => {
        return store.state.storeLayers?.settings;
    });

    const rawElements = computed(() => {
        return store.state.storeLayers?.rawElements;
    });

    const getElementsFromCanvas = (canvas?: CanvasEx | null) => {
        const objects = (canvas?._objects as CanvasElement[]) || [];
        const elements: CanvasElement[] = [];

        for (const object of objects) {
            if (!isValidCanvasElement(object)) continue;
            object.name = object.name ? object.name : '';
            elements.push(object);
        }
        return elements;
    };

    const getPageEpsFromCanvas = (canvas?: CanvasEx | null) => {
        return canvas?.getElementsPageEps() || [];
    };
    const getGroupsFromCanvas = (canvas?: CanvasEx | null) => {
        return canvas?.elementsGroups || [];
    };

    const updateTreeItems = () => {
        if (!rawElements.value) {
            return;
        }
        const treeItems = getTreeItems(rawElements.value);
        store.dispatch('storeLayers/setTreeItems', treeItems);
        store.dispatch('storeLayers/setIsUpdatingTree', false);
    };

    const setRawElements = () => {
        try {
            const { canvas, auxCanvas } = props;
            const pageEpsElements = getPageEpsFromCanvas(canvas);
            const groups = getGroupsFromCanvas(canvas);
            const elements = getElementsFromCanvas(canvas);
            const auxElements = getElementsFromCanvas(auxCanvas);

            store.dispatch('storeLayers/setRawElements', {
                pageEpsElements: pageEpsElements,
                groupElements: groups,
                canvasElements: elements,
                auxCanvasElements: auxElements
            });
        } catch (error) {
            console.error('Error updating groups from canvas', error);
            return;
        }
    };

    const handleSelectionEvent = debounce((elements: ElementType[], event?: PointerEvent) => {
        if (!elements || elements.length <= 0) {
            handleClearSelectionEvent();
            return;
        }

        setTimeout(() => {
            const ids = elements.map((element) => element.uuid || element.id);
            if (ids && ids.length > 0) {
                store.dispatch('storeLayers/setSelectedElementByIds', {
                    ids: ids,
                    event
                });
            }
        });
    }, 400);

    const handleClearSelectionEvent = () => {
        store.dispatch('storeLayers/clearSelectedItems');
        props?.canvas?.discardActiveObject().requestRenderAll();
        props?.auxCanvas?.discardActiveObject().requestRenderAll();
    };

    const handleLayersSelection = (
        data: Event & {
            e?: PointerEvent;
            selected: CanvasElement[];
        }
    ) => {
        // If event is provided, it means the selection is from a user action in the canvas
        if (!data.e) {
            return;
        }
        const selected = data.selected;
        const event = {
            ...data.e,
            ctrlKey: data.e?.shiftKey, // Use ctrlKey for multi-selection
            shiftKey: false // Shift key is not used in this context
        };
        handleSelectionEvent(selected, event);
    };

    const triggerUpdateEvents = () => {
        settings.value?.events?.['updateLayersOrderCanvas']?.();
    };

    const handleGroupEvent = (elements: ElementType[]) => {
        handleSelectionEvent(elements);
    };

    const initializeLayers = debounce(() => {
        try {
            setRawElements();
            updateTreeItems();
            triggerUpdateEvents();
        } catch (error) {
            console.error('Error updating canvas elements', error);
        }
    });

    const setCanvasEventListeners = (canvas = props.canvas) => {
        if (!canvas) return;
        removeCanvasEventListeners(canvas);
        canvas.on('group:added', initializeLayers);
        canvas.on('group:removed', initializeLayers);
        canvas.on('object:added', initializeLayers);
        canvas.on('object:removed', initializeLayers);
        canvas.on('object:grouped', handleGroupEvent);
        canvas.on('object:ungrouped', handleGroupEvent);
        canvas.on('pageeps:added', initializeLayers);
        canvas.on('pageeps:update', initializeLayers);
        canvas.on('pageeps:removed', initializeLayers);
        canvas.on('selection:cloned', handleSelectionEvent);
        canvas.on('selection:created', handleLayersSelection);
        canvas.on('selection:updated', handleLayersSelection);
        canvas.on('selection:cleared', handleClearSelectionEvent);
        canvas.on('canvas:loaded', initializeLayers);
    };

    const removeCanvasEventListeners = (canvas = props.canvas) => {
        if (!canvas) return;
        canvas.off('group:added', initializeLayers);
        canvas.off('group:removed', initializeLayers);
        canvas.off('object:added', initializeLayers);
        canvas.off('object:removed', initializeLayers);
        canvas.off('object:grouped', handleGroupEvent);
        canvas.off('object:ungrouped', handleGroupEvent);
        canvas.off('pageeps:added', initializeLayers);
        canvas.off('pageeps:update', initializeLayers);
        canvas.off('pageeps:removed', initializeLayers);
        canvas.off('selection:cloned', handleSelectionEvent);
        canvas.off('selection:created', handleLayersSelection);
        canvas.off('selection:updated', handleLayersSelection);
        canvas.off('selection:cleared', handleClearSelectionEvent);
        canvas.off('canvas:loaded', initializeLayers);
    };

    return {
        initializeLayers,
        handleSelectionEvent,
        setCanvasEventListeners,
        removeCanvasEventListeners
    };
}
